import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import classNames from 'classnames';

import { ImageSize } from '@nxte-nl/image-hooks';

import { LayoutExtensionContainer } from
  '../../containers/LayoutExtensionContainer/LayoutExtensionContainer';
import { navigationHooks } from '../../hooks/navigationHooks/navigationHooks';
import { routerHooks } from '../../hooks/routerHooks/routerHooks';
import { UrlType } from '../../types/apiEnums';
import { ImagePresentational, LinkPresentational } from '../../types/common';
import { BackArrowLink } from '../BackArrowLink/BackArrowLink';
import { Button, ButtonFit, ButtonIntent } from '../Button/Button';
import { DangerouslySetInnerHtml } from '../DangerouslySetInnerHtml/DangerouslySetInnerHtml';
import { HeroTitle } from '../HeroTitle/HeroTitle';
import { HeroUsp } from '../HeroUsp/HeroUsp';
import { ImageWithSize } from '../ImageWithSize/ImageWithSize';
import { Link, LinkIntent, LinkType } from '../Link/Link';
import styles from './BusinessHeroBlock.module.css';

const messages = defineMessages({
  backArrowLink: {
    id: 'src.components.BusinessHeroBlock.backArrowLink',
    defaultMessage: 'Home',
  },
});

export const hooks = {
  useBackArrowRedirect() {
    const pathname = routerHooks.useSlugPathname();
    const homePageRedirect = navigationHooks.useHomePageRedirect();
    const businessRedirect = navigationHooks.useBusinessPageRedirect();

    return React.useMemo(() => {
      return pathname === `${businessRedirect()}/` ? homePageRedirect() : businessRedirect();
    }, [businessRedirect, homePageRedirect, pathname]);
  },
};

export type Props = {
  heroButton: LinkPresentational | null;
  heroDescription: string | null;
  heroImage: ImagePresentational | null;
  heroTitle: string;
  heroUsp: string | null;
  isDesktop: boolean;
};

export const BusinessHeroBlock: React.FC<Props> = React.memo(props => {
  const {
    heroTitle,
    heroImage,
    heroDescription,
    heroUsp,
    heroButton,
    isDesktop,
  } = props;

  const backArrowRedirect = hooks.useBackArrowRedirect();

  return (
    <div className={styles.businessHeroBlock}>
      <div className={styles.businessHeroBlockInner}>
        <BackArrowLink
          redirect={backArrowRedirect}
          title={<FormattedMessage {...messages.backArrowLink} />}
          linkIntent={LinkIntent.quaternary}
        />

        <div className={styles.content}>
          <div className={styles.leftBlock}>
            <LayoutExtensionContainer>
              {({ layoutExtension }) => (
                <HeroTitle
                  className={styles.title}
                  isEnabledDynamicalSize={true}
                  layoutExtension={layoutExtension}
                >
                  {heroTitle}
                </HeroTitle>
              )}
            </LayoutExtensionContainer>

            {heroDescription ? (
              <DangerouslySetInnerHtml
                variant='bodySubText1'
                size='l'
                className={styles.subTitle}
                dangerousHtml={heroDescription}
              />
            ) : null}

            {heroUsp ? <HeroUsp heroUsp={heroUsp} /> : null}

            {heroButton ? (
              <div className={classNames(styles.buttonsBlock)}>
                <Link
                  type={
                    heroButton.url.type === UrlType.ABSOLUTE ? LinkType.ExternalHref : LinkType.Href
                  }
                  to={heroButton.url.href}
                  withoutHoverStyles
                  isBlock={true}
                >
                  <Button
                    intent={ButtonIntent.primary}
                    fit={isDesktop ? ButtonFit.Inline : ButtonFit.Fill}
                    disabled={false}
                    isLink
                  >
                    {heroButton.title}
                  </Button>
                </Link>
              </div>
            ) : null}
          </div>

          {heroImage ? (
            <div className={styles.heroImageContainer}>
              <ImageWithSize image={heroImage} size={ImageSize.XL} className={styles.heroImage} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
});

BusinessHeroBlock.displayName = 'BusinessHeroBlock';
